import { useMemo, useState } from 'react'
import values from 'lodash/values'
import { Bar, BarChart, CartesianGrid, Cell, XAxis, YAxis } from 'recharts'
import { ChartContainer, ChartTooltip, type ChartConfig } from '@klear/ui/components/chart'
import { Card, CardContent, CardHeader, CardTitle } from '@klear/ui/components/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@klear/ui/components/select'
import { CurrencyReportSelector } from '@klear/ui/components/currency-report-selector'
import { PAST_DUE_BUCKET_KEY } from '@klear/collection/due-chart'
import { useAdvanceRecoveryDueChart } from '@klear/ui/hooks/use-collections'
import { useCurrencyScopedReport } from '@klear/ui/hooks/use-currency-scoped-report'
import { useTraders } from '@klear/ui/hooks/use-traders'
import { Money } from '@klear/money'
import { moneyFormatterFor } from '@klear/money/currency'
import { moneyChartValue } from '@klear/ui/components/chart-series'

const ALL_TRADERS_VALUE = 'all'

type ChartPoint = {
  key: string
  label: string
  amount: number
  offerCount: number
  isPastDue: boolean
}

const chartConfig = {
  amount: { label: 'Remaining', color: '#2563eb' },
  pastDue: { label: 'Past due', color: 'var(--muted-foreground)' }
} satisfies ChartConfig

const PAST_DUE_BAR_FILL = 'var(--color-pastDue)'
const FUTURE_BAR_FILL = 'var(--color-amount)'

export default function CollectionsDueChart() {
  const [traderFilter, setTraderFilter] = useState(ALL_TRADERS_VALUE)
  const selectedTraderId = traderFilter === ALL_TRADERS_VALUE ? undefined : parseInt(traderFilter, 10)

  const { traders = [], isLoading: isTradersLoading } = useTraders()
  const { buckets, isLoading: isChartLoading } = useAdvanceRecoveryDueChart(selectedTraderId)

  const bucketMonies = useMemo(() => buckets.flatMap((bucket) => values(bucket.amountByCurrency)), [buckets])

  const { selectedCurrency, setSelectedCurrency, showCurrencySelector, availableCurrencies } = useCurrencyScopedReport([
    bucketMonies
  ])

  const formatMoney = moneyFormatterFor(selectedCurrency)

  const chartData = useMemo<ChartPoint[]>(
    () =>
      buckets.map((bucket) => ({
        key: bucket.key,
        label: bucket.label,
        amount: moneyChartValue(bucket.amountByCurrency[selectedCurrency] ?? Money.zero(selectedCurrency)),
        offerCount: bucket.offerCount,
        isPastDue: bucket.key === PAST_DUE_BUCKET_KEY
      })),
    [buckets, selectedCurrency]
  )

  const hasData = chartData.some((point) => point.amount > 0)
  const isLoading = isTradersLoading || isChartLoading

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">Advance recovery due</CardTitle>
        <div className="flex items-center gap-2">
          <Select value={traderFilter} onValueChange={setTraderFilter}>
            <SelectTrigger className="w-[200px]" aria-label="Filter by trader">
              <SelectValue placeholder="All traders" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_TRADERS_VALUE}>All traders</SelectItem>
              {traders.map((trader) => (
                <SelectItem key={trader.id} value={`${trader.id}`}>
                  {trader.name ?? `${trader.id}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {showCurrencySelector && (
            <CurrencyReportSelector
              value={selectedCurrency}
              onChange={setSelectedCurrency}
              currencies={availableCurrencies}
            />
          )}
        </div>
      </CardHeader>
      <CardContent className="px-3 pb-4">
        {isLoading ? (
          <div className="flex h-[180px] items-center justify-center text-sm text-muted-foreground">
            Loading chart...
          </div>
        ) : !hasData ? (
          <div className="flex h-[180px] items-center justify-center text-sm text-muted-foreground">
            No remaining advance recovery in this period.
          </div>
        ) : (
          <ChartContainer config={chartConfig} className="h-[180px] w-full">
            <BarChart data={chartData} margin={{ left: 45, right: 10, top: 10, bottom: 10 }}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 11 }} />
              <YAxis
                width={60}
                tickFormatter={(value: number) => formatMoney(value, { notation: 'compact' })}
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 11 }}
              />
              <ChartTooltip
                cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                formatter={(value, _name, item) => {
                  const payload = item.payload as { offerCount?: number }
                  const count = payload.offerCount ?? 0
                  const offerLabel = count === 1 ? '1 offer' : `${count} offers`
                  return [`${formatMoney(value as number)} (${offerLabel})`, 'Remaining']
                }}
                labelFormatter={(label) => label as string}
              />
              <Bar dataKey="amount" radius={[4, 4, 0, 0]}>
                {chartData.map((point) => (
                  <Cell key={point.key} fill={point.isPastDue ? PAST_DUE_BAR_FILL : FUTURE_BAR_FILL} />
                ))}
              </Bar>
            </BarChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  )
}
